import Payment from "@mui/icons-material/Payment";
import Wallet from "@mui/icons-material/Wallet";
import Paypal from "assets/img/svg/paypal";
import classNames from "classnames";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import { deriveBankingMode } from "helpers/bankingStatusResolver";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import PaymentInfo from "./PaymentInfo";
import styles from "./style.module.scss";
import WrongPaymentInfo from "./WrongPaymentInfo";

type MaskState = { [x: string]: boolean };

interface IPaymentBlock {
    beneficiary: IBeneficiar;
    acceptedPaymentMethods: string[];
    companyAcceptedPayments: string;
    maskSensitiveInfo: MaskState;
    setMaskSensitiveInfo: Dispatch<SetStateAction<MaskState>>;
}

// ─── Banking detail sub-components ───────────────────────────────────────────

type SharedInfoProps = { maskSensitiveInfo: MaskState; handleVisibility: (key: string) => void };

const IntlBankingDetails = ({
    meta,
    maskSensitiveInfo,
    handleVisibility,
}: { meta: Record<string, string> } & SharedInfoProps) => {
    const [t] = useTranslation();
    const shared = { maskSensitiveInfo, handleVisibility, maskCount: 2 as const };
    return (
        <>
            <PaymentInfo data={meta.intl_swift} dataType="intl_swift" label="BIC / SWIFT" {...shared} />
            <PaymentInfo
                data={meta.intl_account_number}
                dataType="intl_account_number"
                label={t("IbanModal.IntlAccountNumber")}
                {...shared}
            />
            {meta.intl_routing_code && (
                <PaymentInfo
                    data={meta.intl_routing_code}
                    dataType="intl_routing_code"
                    label={t("IbanModal.IntlRoutingCode")}
                    {...shared}
                />
            )}
            {meta.intl_bank_name && (
                <PaymentInfo
                    data={meta.intl_bank_name}
                    dataType="intl_bank_name"
                    label={t("IbanModal.IntlBankName")}
                    {...shared}
                />
            )}
        </>
    );
};

const IbanBankingDetails = ({
    beneficiary,
    maskSensitiveInfo,
    handleVisibility,
}: { beneficiary: IBeneficiar } & SharedInfoProps) => {
    const [t] = useTranslation();
    const shared = { maskSensitiveInfo, handleVisibility };
    return (
        <>
            <PaymentInfo data={beneficiary.iban} dataType="iban" label={t("Beneficiars.IBAN")} {...shared} />
            <PaymentInfo
                data={beneficiary.swift}
                dataType="swift"
                label={t("Beneficiars.Swift")}
                maskCount={2}
                {...shared}
            />
            {beneficiary.routingNumber && (
                <PaymentInfo
                    data={beneficiary.routingNumber}
                    dataType="routingNumber"
                    label={t("Beneficiars.RoutingNumber")}
                    maskCount={2}
                    {...shared}
                />
            )}
        </>
    );
};

const MetadataBankingDetails = ({
    beneficiary,
    fields,
    maskSensitiveInfo,
    handleVisibility,
}: { beneficiary: IBeneficiar; fields: string[] } & SharedInfoProps) => {
    const [t] = useTranslation();
    return (
        <>
            {fields.map((fieldKey) => (
                <PaymentInfo
                    key={fieldKey}
                    data={beneficiary.bankingMetadata?.[fieldKey] ?? ""}
                    dataType={fieldKey}
                    label={t(`Beneficiary.${fieldKey}`)}
                    maskSensitiveInfo={maskSensitiveInfo}
                    handleVisibility={handleVisibility}
                    maskCount={2}
                />
            ))}
        </>
    );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const PaymentBlock = ({
    beneficiary,
    acceptedPaymentMethods,
    companyAcceptedPayments,
    maskSensitiveInfo,
    setMaskSensitiveInfo,
}: IPaymentBlock) => {
    const [t] = useTranslation();
    const { countryFeatures, internationalBankingEnabled, companyCountryCode } = useCompanySettings();

    const usesIbanBanking = countryFeatures.bankingFields.includes("iban");
    const metadataFields = countryFeatures.bankingFields.filter((f) => f !== "iban" && f !== "bic");
    const meta = (beneficiary.bankingMetadata ?? {}) as Record<string, string>;

    const isInternational = (() => {
        if (!internationalBankingEnabled) return false;
        const bankCountry = meta.bank_account_country;
        if (!bankCountry) return !!meta.intl_swift; // backward compat : pas de bank_account_country sauvegardé
        return (
            deriveBankingMode(bankCountry, companyCountryCode, countryFeatures.bankingStatusRules) === "international"
        );
    })();

    const handleVisibility = (key: string) =>
        setMaskSensitiveInfo({ ...maskSensitiveInfo, [key]: !maskSensitiveInfo[key] });

    const usesPaymentMethod = (payment: "rib" | "paypal" | "check") =>
        acceptedPaymentMethods?.includes(payment) && companyAcceptedPayments.includes(payment);

    const sharedInfoProps = { maskSensitiveInfo, handleVisibility };

    return (
        <div className={styles["beneficiary-payment-block"]}>
            <div className={styles["title"]}>{t("Beneficiars.Payment")}</div>

            {usesPaymentMethod("rib") && (
                <div className={styles["info-string"]}>
                    <span>
                        <Payment />
                        {t("Beneficiars.Bank")}
                        {beneficiary.iban && !beneficiary.validBankInfos && usesIbanBanking && <WrongPaymentInfo />}
                    </span>
                    <div className={styles["info-string-bloc-right"]}>
                        <p>{beneficiary.ribOwnerName}</p>
                        {isInternational ? (
                            <IntlBankingDetails meta={meta} {...sharedInfoProps} />
                        ) : usesIbanBanking ? (
                            <IbanBankingDetails beneficiary={beneficiary} {...sharedInfoProps} />
                        ) : (
                            <MetadataBankingDetails
                                beneficiary={beneficiary}
                                fields={metadataFields}
                                {...sharedInfoProps}
                            />
                        )}
                    </div>
                </div>
            )}

            {usesPaymentMethod("paypal") && (
                <div className={classNames(styles["info-string"], styles["paypal-option"])}>
                    <span>
                        <Paypal />
                        {t("Beneficiars.Paypal")}
                    </span>
                    <PaymentInfo
                        data={beneficiary.paypalEmail}
                        dataType="paypal"
                        label={t("Beneficiars.Paypal")}
                        {...sharedInfoProps}
                    />
                </div>
            )}

            {usesPaymentMethod("check") && (
                <div className={styles["info-string"]}>
                    <span>
                        <Wallet />
                        {t("Beneficiars.Check")}
                    </span>
                    <span id="is-accepted-check">
                        {beneficiary.bankCheck ? t("Beneficiars.PrecountYes") : t("Beneficiars.PrecountNo")}
                    </span>
                </div>
            )}
        </div>
    );
};

export default PaymentBlock;
